import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import { useStore } from "../contexts/StoreContext";
import { useNotification } from "../contexts/NotificationContext";
import { fetchCustomer, fetchCustomerByUuid } from "../api/queries";
import Header from "../components/Header";
import { Button } from "../components/Button";
import SendPassModal from "./customers/SendPassModal";
import {
  customFieldRows,
  formatCustomFieldValue,
} from "../utils/customFields";

// The URL carries the customer's uuid — what the list shows and links. Links
// from before that carried the numeric id, and a uuid can itself be all digits
// (8 hex chars), so a number is tried as a uuid first and only then as an id.
// That first try is silent: its 404 is expected, not an error to toast.
async function loadCustomer(storeId, ref) {
  const mayBeLegacyId = /^\d+$/.test(ref);
  try {
    return await fetchCustomerByUuid(storeId, ref, { silent: mayBeLegacyId });
  } catch (error) {
    if (mayBeLegacyId && error.response?.status === 404) {
      return fetchCustomer(storeId, ref);
    }
    throw error;
  }
}

export default function CustomerShow() {
  const { t } = useLanguage();
  const { selectedStore } = useStore();
  const { showNotification } = useNotification();
  const { ref } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSendPassOpen, setIsSendPassOpen] = useState(false);

  const { data: customer, isLoading } = useQuery({
    queryKey: ["customer", selectedStore?.id, ref],
    queryFn: () => loadCustomer(selectedStore?.id, ref),
    enabled: !!selectedStore?.id && !!ref,
  });

  // An old id link resolved: move the address to the uuid, seeding the cache
  // so the swap costs no second request.
  useEffect(() => {
    if (customer?.uuid && customer.uuid !== ref) {
      queryClient.setQueryData(
        ["customer", selectedStore?.id, customer.uuid],
        customer,
      );
      navigate(`/customers/${encodeURIComponent(customer.uuid)}`, {
        replace: true,
      });
    }
  }, [customer, ref, selectedStore?.id, queryClient, navigate]);

  const customFields = customFieldRows(
    customer?.custom_fields,
    selectedStore?.custom_fields,
  );

  const handleSendPassClose = (result) => {
    setIsSendPassOpen(false);
    if (result?.sentTo) {
      showNotification(
        t("customers.sendPass.title"),
        t("customers.sendPass.success", { email: result.sentTo }),
        "success",
        [],
        5000,
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <Header
        title={customer?.name || t("customers.unnamed")}
        description={t("customers.show.description")}
        rightSection={
          /* Not gated on canManageStore. Handing a customer their own card
             back is counter work, not administration — a member who can
             already open this page gains nothing they did not have. The
             backend agrees: send_pass is no longer admin-only. Editing,
             deleting and exporting stay behind canManageStore elsewhere.

             Scanner-only members never reach this page at all; the server
             allowlists them down to index/show/scan for their store. */
          <Button
            variant="secondary"
            icon={EnvelopeIcon}
            onClick={() => setIsSendPassOpen(true)}
          >
            {t("customers.sendPass.button")}
          </Button>
        }
      />

      <SendPassModal
        isOpen={isSendPassOpen}
        onClose={handleSendPassClose}
        customer={customer}
        storeId={selectedStore?.id}
      />

      {/* Customer Details Card */}
      <div></div>
      <div className="">
        <h3 className="text-lg leading-6 font-medium text-gray-900 pb-4">
          {t("customers.show.details")}
        </h3>
        <div className="bg-white shadow-sm overflow-hidden sm:rounded-lg border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("customers.table.email")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {customer?.email || "-"}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("customers.table.phone")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {customer?.phone || "-"}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("customers.table.birthday")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {customer?.birthday || "-"}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("customers.table.points")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {customer?.point || "0"}
              </dd>
            </div>
            {/* Only stamp cards with a next tier configured return this, so
                its presence is the whole condition — an untiered card shows
                nothing rather than an empty row. */}
            {customer?.tier_progress && (
              <>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("customers.show.tier")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {customer.tier_progress.current_pass_name}
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("customers.show.tierProgress")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {t("customers.show.tierProgressValue", {
                      completed: customer.tier_progress.cycles_completed,
                      required: customer.tier_progress.cycles_required,
                      next: customer.tier_progress.next_pass_name,
                    })}
                  </dd>
                </div>
              </>
            )}
          </dl>
        </div>
      </div>

      {/* What the signup form collected beyond the standard columns, hidden
          fields (e.g. an ad click id) included. Read-only, so shown to members
          too — the edit drawer that also lists them is admin-only. */}
      {customFields.length > 0 && (
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900 pb-4">
            {t("customers.show.customFields")}
          </h3>
          <div className="bg-white shadow-sm overflow-hidden sm:rounded-lg border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              {customFields.map(({ key, label, value }) => (
                <div key={key} className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">{label}</dt>
                  <dd className="mt-1 text-sm text-gray-900 break-words">
                    {formatCustomFieldValue(value, t)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div>
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <h3 className="text-lg leading-6 font-medium text-gray-900 pb-4">
              {t("transactions.title")}
            </h3>
            <table className="min-w-full divide-y divide-gray-300 text-sm text-left whitespace-nowrap">
              <thead className="bg-gray-50">
                <tr className="font-semibold text-gray-900">
                  <th scope="col" className="p-3 pl-4">
                    {t("transactions.table.date")}
                  </th>
                  <th scope="col" className="p-3">
                    {t("transactions.table.type")}
                  </th>
                  <th scope="col" className="p-3">
                    {t("transactions.table.user")}
                  </th>
                  <th scope="col" className="p-3">
                    {t("transactions.table.amount")}
                  </th>
                  <th scope="col" className="p-3">
                    {t("transactions.table.points")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {customer?.transactions?.map((transaction) => (
                  <tr key={transaction.id} className="text-gray-500">
                    <td className="p-3 pl-4">
                      {new Date(transaction.created_at).toLocaleString()}
                    </td>
                    <td className="p-3">
                      {transaction.kind.toLocaleUpperCase()}
                    </td>
                    <td className="p-3">{transaction.user?.name}</td>
                    <td className="p-3">
                      {transaction.amount > 0
                        ? `${selectedStore?.currency} ${transaction.amount}`
                        : ``}
                    </td>
                    <td className="p-3">{transaction.point}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
